import React from 'react';
import styles from './HeroSection.module.css';
import heroWomanMobile from '../../assets/images/hero-woman-mobile.png';
import heroWomanTablet from '../../assets/images/hero-woman-tablet.png';
import heroWomanDesktop from '../../assets/images/hero-woman-desktop.png';

interface HeroSectionProps {
  onSignInClick: () => void;
}

const HeroSection: React.FC<HeroSectionProps> = ({ onSignInClick }) => {
  return (
    <div
      className={`${styles.heroContainer} overflow-visible`}
      data-name="hero section"
      id="node-12_9943"
    >
      {/* Clean Background - No Glass Effects */}
      <div className="absolute inset-0 bg-white" />

      {/* Main Content Grid - with max-width for ultra-wide monitors */}
      <div className={`relative z-5 h-full ${styles.heroContent}`}>
        {/* Mobile Layout - Stacked */}
        <div className="md:hidden h-full flex flex-col pt-20">
          {/* Text Content - Mobile */}
          <div className="flex-1 flex items-center justify-center px-2 sm:px-4 py-8">
            <div className="w-full max-w-lg">
              <div className="flex flex-col gap-3 items-center text-center">
                <h1 className="font-['Ovo',_serif] text-[18px] sm:text-[20px] leading-[1.1] uppercase text-[#1f1f1f] whitespace-nowrap">
                  DO YOU HAVE CLOUT?
                </h1>
                <h2 className="font-['Ovo',_serif] text-[12px] sm:text-[14px] leading-[1.2] uppercase text-[#1f1f1f] whitespace-nowrap">
                  FIND YOUR SCORE. JOIN THE MOVEMENT!
                </h2>
                <div
                  className={`font-['Open_Sans',_sans-serif] font-normal ${styles.fontVariation}`}
                >
                  <p className="leading-[1.6] text-[14px] text-[#1f1f1f] text-justify">
                    Revolutionizing the Aesthetic Industry. We combine reviews, education,
                    experience and online presence into one powerful metric:{' '}
                    <span
                      className={`font-['Open_Sans',_sans-serif] font-semibold ${styles.fontVariation}`}
                    >
                      The CLOUT Score
                    </span>
                  </p>
                </div>
                <button
                  className="w-full flex items-center justify-center px-6 py-3 mt-4 border-2 border-[#1f1f1f] bg-white hover:bg-[#1f1f1f] text-[#1f1f1f] hover:text-white transition-all duration-200 cursor-pointer min-h-[48px] touch-manipulation"
                  onClick={onSignInClick}
                >
                  <span className="font-['Ovo',_serif] text-[14px] text-center uppercase tracking-wider font-medium">
                    CHECK MY CLOUT SCORE
                  </span>
                </button>
              </div>
            </div>
          </div>

          {/* Image - Mobile */}
          <div className="flex-1 flex items-center justify-center">
            <img
              src={heroWomanMobile}
              alt="Professional woman in healthcare setting"
              className="w-full h-full max-h-[400px] object-contain"
              loading="eager"
            />
          </div>
        </div>

        {/* Desktop/Tablet Layout - Grid-based with clear zones */}
        <div className="hidden md:block h-full relative">
          {/* Background Images - full width background extending under navbar */}
          <div className="absolute inset-0 -top-10">
            {/* Tablet Background Image */}
            <img
              src={heroWomanTablet}
              alt="Professional woman in healthcare setting"
              className="hidden md:block lg:hidden absolute top-0 right-0 w-full h-[calc(100%+5rem)] object-cover z-0"
              style={{ transform: 'translateX(5%)', objectFit: 'cover' }}
              loading="eager"
            />

            {/* Desktop Background Image */}
            <img
              src={heroWomanDesktop}
              alt="Professional woman in healthcare setting"
              className="hidden lg:block absolute top-0 right-0 w-full h-[calc(100%+5rem)] object-cover z-0"
              style={{ transform: 'translateX(5%)', objectFit: 'cover' }}
              loading="eager"
            />
          </div>

          {/* Content Grid - Split layout */}
          <div className="relative z-10 h-full grid grid-cols-5 md:grid-cols-3 lg:grid-cols-5 pt-20">
            {/* Text Content Zone - Left side */}
            <div className="col-span-3 md:col-span-2 lg:col-span-3 flex items-center justify-center md:justify-start">
              <div className="w-full max-w-xl xl:max-w-2xl 2k:max-w-3xl px-8 md:px-12 lg:px-16 xl:px-20 2k:px-24 md:ml-4 lg:ml-8 xl:ml-12 2k:ml-16">
                <div className="flex flex-col gap-4 lg:gap-6 items-start text-left">
                  <h1 className="font-['Ovo',_serif] text-[28px] md:text-[32px] lg:text-[38px] xl:text-[44px] 2k:text-[48px] leading-[1.1] uppercase text-[#1f1f1f] whitespace-nowrap">
                    DO YOU HAVE CLOUT?
                  </h1>
                  <h2 className="font-['Ovo',_serif] text-[16px] md:text-[18px] lg:text-[22px] xl:text-[24px] 2k:text-[28px] leading-[1.2] uppercase text-[#1f1f1f] whitespace-nowrap">
                    FIND YOUR SCORE. JOIN THE MOVEMENT!
                  </h2>
                  <div
                    className={`font-['Open_Sans',_sans-serif] font-normal ${styles.fontVariation}`}
                  >
                    <p className="leading-[1.6] text-[14px] md:text-[16px] lg:text-[18px] xl:text-[20px] 2k:text-[22px] text-[#1f1f1f] text-justify max-w-md 2k:max-w-lg">
                      Revolutionizing the Aesthetic Industry. We combine reviews, education,
                      experience and online presence into one powerful metric:{' '}
                      <span
                        className={`font-['Open_Sans',_sans-serif] font-semibold ${styles.fontVariation}`}
                      >
                        The CLOUT Score
                      </span>
                    </p>
                  </div>
                  <button
                    className="flex items-center justify-center px-6 md:px-8 py-3 mt-4 lg:mt-6 border-2 border-[#1f1f1f] bg-white hover:bg-[#1f1f1f] text-[#1f1f1f] hover:text-white transition-all duration-200 cursor-pointer min-h-[48px] touch-manipulation shadow-sm hover:shadow-md"
                    onClick={onSignInClick}
                  >
                    <span className="font-['Ovo',_serif] text-[12px] md:text-[14px] lg:text-[15px] text-center uppercase tracking-wider font-medium">
                      CHECK MY CLOUT SCORE
                    </span>
                  </button>
                </div>
              </div>
            </div>

            {/* Image Zone - Right side (reserved for woman's image) */}
            <div className="col-span-2 md:col-span-1 lg:col-span-2">
              {/* This space is intentionally left empty - the image is positioned as background */}
              {/* The woman's image appears here through the background positioning above */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;
